import { Divider, Skeleton, Stack, Typography } from '@mui/material'
import { SkeletonText } from 'utoniq-ui'

import {
  EventStatus,
  Fee,
  FirstComeFirstServedDetailForGuestFragment,
  FirstComeFirstServedDetailFragment,
  Maybe,
  PaymentDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { FirstComeFirstServedContentCell } from '@/components/pages/FirstComeFirstServedDetail/template/components'
import {
  ApplyPreSaleFirstComeFirstServedValue,
  FirstComeFirstServedDetailForm,
} from '@/components/pages/FirstComeFirstServedDetail/template/FirstComeFirstServedDetailForm'
import { User, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { MembershipCell } from './components/MembershipCell'

export * from './fragments'

export type State = {
  user: User | null
  loading: boolean
  preSaleId: string
  preSale:
    | FirstComeFirstServedDetailFragment
    | FirstComeFirstServedDetailForGuestFragment
    | undefined
  paymentSource?: Maybe<PaymentDetailFragment>
  originFullNameValue?: string
  paymentFees?: Maybe<Fee[]>
  choices:
    | { itemId: string; skuId: string | undefined; count: number }
    | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: (
    values: ApplyPreSaleFirstComeFirstServedValue | undefined
  ) => Promise<void>
  onReadyPaymentMethod: (
    values: ApplyPreSaleFirstComeFirstServedValue | undefined
  ) => Promise<void>
}

export const FirstComeFirstServedDetailTemplate: Template<State, Action> = ({
  state,
  onSubmit,
  onReadyPaymentMethod,
}) => {
  const { t } = useTranslation('firstComeFirstServedDetail')
  const { routerNames, routerPages, generatePath } = useRouter()

  return (
    <BaseLayout
      margin
      header={{
        back: true,
        defaultBackPage:
          state.preSale?.event?.status === EventStatus.Published
            ? generatePath(routerPages.ShokufesEventDetail, {
                eventId: state.preSale?.event?.id ?? '#',
              })
            : undefined,
        title: t('title', '販売情報詳細'),
      }}
    >
      <Head title={routerNames.FirstComeFirstServedDetail} />
      {state.loading || !state.preSale ? (
        <>
          <FirstComeFirstServedTitleSkeletonCell />
          <FirstComeFirstServedSkeletonCell />
        </>
      ) : (
        <>
          <FirstComeFirstServedTitleCell title={state.preSale.title} />
          <FirstComeFirstServedContentCell preSale={state.preSale} />
          {state.preSale.isPremium && (
            <MembershipCell preSale={state.preSale} />
          )}
          <FirstComeFirstServedDetailForm
            preSaleId={state.preSaleId}
            preSale={state.preSale}
            originFullNameValue={state.originFullNameValue}
            paymentSource={state.paymentSource}
            onSubmit={onSubmit}
            onReadyPaymentMethod={onReadyPaymentMethod}
            paymentFees={state.paymentFees}
            choices={state.choices}
          />
        </>
      )}
    </BaseLayout>
  )
}

type FirstComeFirstServedTitleCellProps = {
  title: string
}
const FirstComeFirstServedTitleCell: Cell<FirstComeFirstServedTitleCellProps> =
  ({ title }) => {
    return (
      <CellFrame sx={{ pt: 3, pb: 5 }}>
        <Typography variant="h4">{title}</Typography>
      </CellFrame>
    )
  }

const FirstComeFirstServedTitleSkeletonCell: Cell = () => {
  return (
    <CellFrame sx={{ pt: 3, pb: 5 }}>
      <SkeletonText variant="h4" width="50%" />
    </CellFrame>
  )
}

const FirstComeFirstServedSkeletonCell: Cell = () => {
  return (
    <CellFrame>
      <Stack sx={{ pb: 5 }}>
        <Stack direction="row" spacing={1}>
          <Stack width="10%">
            <SkeletonText variant="body2" />
          </Stack>
          <Stack flex={1}>
            <SkeletonText variant="body2" />
          </Stack>
        </Stack>
        <Stack sx={{ pt: 2 }} spacing={1}>
          <SkeletonText numberOfLine={3} variant="body2" />
        </Stack>
        <Stack sx={{ py: 2 }} spacing={1}>
          <SkeletonText numberOfLine={2} variant="body2" />
        </Stack>
        <Stack sx={{ py: 2 }} spacing={1}>
          <SkeletonText numberOfLine={2} variant="body2" />
        </Stack>
        <Stack sx={{ py: 2 }} spacing={1}>
          <SkeletonText numberOfLine={2} variant="body2" />
        </Stack>
      </Stack>
      <Stack sx={{ pb: 5 }}>
        <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
          <SkeletonText variant="h4" />
        </Stack>
        <Stack
          sx={{
            borderColor: 'grey.100',
            borderWidth: 1,
            borderStyle: 'solid',
            borderRadius: 2,
            px: 4,
            py: 5,
            my: 4,
          }}
          spacing={1}
        >
          <SkeletonText variant="caption" width="25%" />
          <SkeletonText variant="caption" width="50%" />
        </Stack>
      </Stack>
      <Stack sx={{ pb: 5 }}>
        <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
          <SkeletonText variant="h4" />
        </Stack>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ py: 5 }}>
          <Stack flex={1}>
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Stack>
          <Stack flex={1}>
            <SkeletonText variant="caption" width="50%" />
          </Stack>
        </Stack>
        <Divider />
      </Stack>
      {Array.from(Array(2).keys()).map(key => (
        <Stack sx={{ pb: 5, pt: 3 }} key={key}>
          <Stack sx={{ py: 1, bgcolor: 'grey.50' }}>
            <SkeletonText variant="h4" />
          </Stack>
          <Stack spacing={3}>
            <SkeletonText variant="body2" numberOfLine={3} />
          </Stack>
        </Stack>
      ))}
      <Stack flex={1} sx={{ py: 4 }} alignItems="center">
        <Skeleton
          variant="rectangular"
          width={244}
          height={44}
          sx={{ borderRadius: '34px' }}
        />
      </Stack>
    </CellFrame>
  )
}
